import { Helmet } from "react-helmet-async";
import { absoluteUrl } from "@/lib/seo";

type SeoProps = {
  title: string;
  description: string;
  /** Path relative to the site root (e.g. "/projects/foo") or full URL. Defaults to current pathname. */
  url?: string;
  /** Absolute image URL recommended (≥1200×630 for OG). */
  image?: string | null;
  type?: "website" | "article" | "profile";
  keywords?: string[];
  locale?: string;
  /** Single JSON-LD object or array. Each is rendered as its own <script>. */
  jsonLd?: object | object[];
  /** Set true on private/admin pages. */
  noIndex?: boolean;
  /** Article-specific. */
  publishedTime?: string;
  modifiedTime?: string;
  /** Optional geo metadata for GEO/local SEO. Region defaults to "LK". */
  geo?: { lat?: number; lng?: number; placeName?: string; region?: string };
  /** Optional hreflang alternates: [{ hrefLang: 'en', href: '/...' }, ...] */
  alternates?: { hrefLang: string; href: string }[];
};

const SITE_NAME = "Raise Sri Lanka";
const DEFAULT_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/e54fac04-6fef-489d-8aa1-765bc8839b7d";

export function Seo({
  title,
  description,
  url,
  image,
  type = "website",
  keywords,
  locale = "en_US",
  jsonLd,
  noIndex,
  publishedTime,
  modifiedTime,
  geo,
  alternates,
}: SeoProps) {
  const canonical = absoluteUrl(
    url ?? (typeof window !== "undefined" ? window.location.pathname : "/"),
  );
  const ogImage = image || DEFAULT_IMAGE;
  const fullTitle = title.length > 60 ? title.slice(0, 57) + "…" : title;
  // Pad very short descriptions with a site suffix so they meet the 50-char SEO minimum,
  // and only hard-truncate when the content actually exceeds the 160-char OG/SERP cap.
  const SUFFIX = " — Raise Sri Lanka, a US 501(c)(3) funding community projects across Sri Lanka.";
  const padded = description.length < 50 ? (description + SUFFIX).trim() : description;
  const desc = padded.length > 160 ? padded.slice(0, 157) + "…" : padded;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const region = geo?.region ?? "LK";

  // Default x-default + en alternates pointing to canonical (single-language site).
  const alts = alternates?.length
    ? alternates.map((a) => ({ ...a, href: absoluteUrl(a.href) }))
    : [
        { hrefLang: "en", href: canonical },
        { hrefLang: "x-default", href: canonical },
      ];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords?.length ? <meta name="keywords" content={keywords.join(", ")} /> : null}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noIndex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1"} />
      <html lang="en" />

      {/* hreflang alternates */}
      {alts.map((a) => (
        <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content={locale} />
      {publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}
      {modifiedTime ? <meta property="article:modified_time" content={modifiedTime} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Geo (helps local/AEO results) */}
      <meta name="geo.region" content={region} />
      {geo?.placeName ? <meta name="geo.placename" content={geo.placeName} /> : null}
      {typeof geo?.lat === "number" && typeof geo?.lng === "number" ? (
        <>
          <meta name="geo.position" content={`${geo.lat};${geo.lng}`} />
          <meta name="ICBM" content={`${geo.lat}, ${geo.lng}`} />
        </>
      ) : null}

      {/* JSON-LD */}
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}
